import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";

import { MESSAGES } from "@/i18n/messages";
import type { LocaleCode, Messages } from "@/i18n/types";
import { DEFAULT_LOCALE, LOCALES } from "@/i18n/types";

export interface IntlHelpers {
  formatDate: (value: Date | number | string, options?: Intl.DateTimeFormatOptions) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatRelativeTime: (
    value: number,
    unit: Intl.RelativeTimeFormatUnit,
    options?: Intl.RelativeTimeFormatOptions,
  ) => string;
}

interface LocaleContextValue {
  locale: LocaleCode;
  locales: readonly LocaleCode[];
  messages: Messages;
  t: (path: string, vars?: Record<string, string | number>) => string;
  intl: IntlHelpers;
  localePath: (path: string, targetLocale?: LocaleCode) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function getMessageAtPath(messages: Messages, path: string): string | undefined {
  const segments = path.split(".");
  let current: unknown = messages;
  for (const segment of segments) {
    if (current && typeof current === "object" && segment in (current as Record<string, unknown>)) {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }
  return typeof current === "string" ? current : undefined;
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = vars[key];
    return value === undefined ? match : String(value);
  });
}

interface LocaleProviderProps {
  children: ReactNode;
  locale: LocaleCode;
}

export function LocaleProvider({ children, locale }: LocaleProviderProps): JSX.Element {
  const safeLocale: LocaleCode = (LOCALES as readonly string[]).includes(locale)
    ? locale
    : DEFAULT_LOCALE;
  const messages = MESSAGES[safeLocale];

  const t = useCallback(
    (path: string, vars?: Record<string, string | number>) => {
      const primary = getMessageAtPath(messages, path);
      if (primary !== undefined) return interpolate(primary, vars);
      const fallback = getMessageAtPath(MESSAGES[DEFAULT_LOCALE], path);
      return fallback !== undefined ? interpolate(fallback, vars) : path;
    },
    [messages],
  );

  const intl = useMemo<IntlHelpers>(() => {
    return {
      formatDate: (value, options) =>
        new Intl.DateTimeFormat(safeLocale, options).format(
          value instanceof Date ? value : new Date(value),
        ),
      formatNumber: (value, options) =>
        new Intl.NumberFormat(safeLocale, options).format(value),
      formatRelativeTime: (value, unit, options) =>
        new Intl.RelativeTimeFormat(safeLocale, options).format(value, unit),
    };
  }, [safeLocale]);

  const localePath = useCallback(
    (path: string, targetLocale?: LocaleCode) => {
      const target = targetLocale ?? safeLocale;
      if (path.startsWith("http://") || path.startsWith("https://")) return path;
      const normalized = path.startsWith("/") ? path : `/${path}`;
      if (normalized === "/") return `/${target}`;
      return `/${target}${normalized}`;
    },
    [safeLocale],
  );

  const value = useMemo<LocaleContextValue>(
    () => ({
      intl,
      locale: safeLocale,
      locales: LOCALES,
      localePath,
      messages,
      t,
    }),
    [intl, localePath, messages, safeLocale, t],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used inside a LocaleProvider.");
  }
  return context;
}

export function useT(): LocaleContextValue["t"] {
  return useLocale().t;
}

export function useIntl(): IntlHelpers {
  return useLocale().intl;
}
